import { useMemo } from 'react';
import { Collaborator } from '@/features/Collaborators/Models/collaborator';
import { useSalaryPermissions } from './useSalaryPermissions';

/**
 * Hook to filter salary-related fields from collaborators data
 * based on user permissions.
 *
 * This is a CRITICAL security measure to ensure salary data
 * is never sent to the frontend for users without proper permissions.
 */
export const useSecureCollaborators = (collaborators: Collaborator[]): Collaborator[] => {
  const { canViewSalary } = useSalaryPermissions();

  return useMemo(() => {
    // If user can view salary, return all data
    if (canViewSalary) {
      return collaborators;
    }

    // Otherwise, remove ALL salary-related fields
    return collaborators.map(collaborator => {
      // Create a copy without salary fields
      const {
        // Remove old salary fields (pre-migration)
        newSalaryEntryAmount,
        newSalaryEntryDate,
        newSalaryEntryExtras,
        newSalaryEntryObservations,
        newSalaryEntryReason,
        newSalaryEntrySalaryType,
        salaryHistoryAllowRemove,
        salaryHistorySalaries,
        salaryHistoryShowHistory,
        // Keep the rest
        ...safeData
      } = collaborator as any;

      return safeData as Collaborator;
    });
  }, [collaborators, canViewSalary]);
};

/**
 * List of salary-related field names that should be removed
 * from collaborators data for users without salary permissions.
 */
export const SALARY_FIELD_NAMES = [
  // Old fields (pre-migration)
  'newSalaryEntryAmount',
  'newSalaryEntryDate',
  'newSalaryEntryExtras',
  'newSalaryEntryObservations',
  'newSalaryEntryReason',
  'newSalaryEntrySalaryType',
  'salaryHistoryAllowRemove',
  'salaryHistorySalaries',
  'salaryHistoryShowHistory',
] as const;

/**
 * Check if a field name is salary-related
 */
export const isSalaryField = (fieldName: string): boolean => {
  return SALARY_FIELD_NAMES.includes(fieldName as any);
};